import {
  SCHEDULE_MAX_LEAD_DAYS,
  SCHEDULE_MIN_LEAD_MINUTES,
  type ContentWarning,
} from '@zuruflex/types';
import {
  Alert,
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
} from '@zuruflex/ui';
import { AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PublishDialogProps {
  nextVersion: number;
  isAgreement: boolean;
  warnings: ContentWarning[];
  pending: boolean;
  onCancel: () => void;
  onPublish: () => void;
  onSchedule: (atIso: string) => void;
}

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" in the browser's own zone. */
function localInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * The confirmation in front of publishing: what is worth a second look,
 * and the choice between now and later.
 *
 * The checks are advice, never a gate — an owner correcting a mistake on
 * the live site must not be argued with by a checklist.
 */
export function PublishDialog({
  nextVersion,
  isAgreement,
  warnings,
  pending,
  onCancel,
  onPublish,
  onSchedule,
}: PublishDialogProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [mode, setMode] = useState<'now' | 'later'>('now');
  const earliest = useMemo(() => new Date(Date.now() + (SCHEDULE_MIN_LEAD_MINUTES + 1) * 60_000), []);
  const [when, setWhen] = useState(() => localInput(new Date(Date.now() + 60 * 60_000)));

  const at = new Date(when);
  const valid =
    mode === 'now' ||
    (!Number.isNaN(at.getTime()) &&
      at.getTime() >= earliest.getTime() - 60_000 &&
      at.getTime() <= Date.now() + SCHEDULE_MAX_LEAD_DAYS * 86_400_000);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('admin:content.confirm.publishTitle', { version: nextVersion })}</DialogTitle>
        <DialogDescription>{t('admin:content.confirm.publishBody')}</DialogDescription>
      </DialogHeader>

      {isAgreement ? <Alert variant="warning">{t('admin:content.confirm.agreementWarning')}</Alert> : null}

      {warnings.length > 0 ? (
        <div className="mb-3 rounded-xl border border-warning/40 bg-warning/5 p-3 text-sm" role="note">
          <p className="mb-2 flex items-center gap-2 font-medium text-warning">
            <AlertTriangle className="size-4" aria-hidden />
            {t('admin:content.check.title', { count: warnings.length })}
          </p>
          <ul className="space-y-1 text-text-muted">
            {warnings.map((warning) => (
              <li key={`${warning.code}:${warning.detail ?? ''}`}>
                {t(`admin:content.check.${warning.code}`, {
                  count: warning.count,
                  detail: warning.detail ?? '',
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <fieldset className="space-y-2">
        <legend className="sr-only">{t('admin:content.schedule.when')}</legend>
        {(['now', 'later'] as const).map((value) => (
          <label
            key={value}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm',
              mode === value ? 'border-accent bg-accent/10' : 'border-border-default',
            )}
          >
            <input
              type="radio"
              name="publish-mode"
              checked={mode === value}
              onChange={() => setMode(value)}
              className="accent-[var(--color-accent)]"
            />
            {t(`admin:content.schedule.${value}`)}
          </label>
        ))}
        {mode === 'later' ? (
          <div className="space-y-1 pl-1">
            <Input
              type="datetime-local"
              value={when}
              min={localInput(earliest)}
              onChange={(event) => setWhen(event.target.value)}
              aria-label={t('admin:content.schedule.at')}
            />
            <p className="text-xs text-text-muted">
              {t('admin:content.schedule.hint', { minutes: SCHEDULE_MIN_LEAD_MINUTES })}
            </p>
          </div>
        ) : null}
      </fieldset>

      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>
          {t('common:action.cancel')}
        </Button>
        <Button
          isLoading={pending}
          disabled={!valid}
          onClick={() => (mode === 'now' ? onPublish() : onSchedule(at.toISOString()))}
        >
          {mode === 'now' ? t('admin:content.editor.publish') : t('admin:content.schedule.submit')}
        </Button>
      </DialogFooter>
    </>
  );
}
